import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabaseService } from '../../services/supabaseService';
import { portalService } from '../../services/portalService';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import LoadingSpinner from '../../components/LoadingSpinner';

const { FiUsers, FiShield, FiEdit, FiPlus, FiX, FiSave, FiTrash2, FiRefreshCw, FiBuilding, FiBarChart3, FiChevronDown, FiChevronUp } = FiIcons;

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [allAssets, setAllAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    roles: ['Viewer'],
    companyAccess: [],
    assetPermissions: {}
  });

  const availableRoles = ['Admin', 'Executive', 'Manager', 'Viewer'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [userData, companiesData] = await Promise.all([
        supabaseService.getAllUsers(),
        portalService.getUserCompanies()
      ]);
      
      setUsers(userData);
      setCompanies(companiesData);

      // Fetch all assets for asset-level permissions
      const assetsMap = {};
      for (const company of companiesData) {
        const assets = await portalService.getCompanyAssets(company.id);
        assetsMap[company.id] = assets;
      }
      setAllAssets(assetsMap);

      // Fetch user permissions for each user
      const usersWithPermissions = await Promise.all(
        userData.map(async (user) => {
          const permissions = await supabaseService.getUserPermissions(user.user_id || user.id);
          return {
            ...user,
            companyAccess: permissions.companies || [],
            assetPermissions: permissions.assets || {}
          };
        })
      );
      setUsers(usersWithPermissions);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let userId;
      
      if (editingId) {
        // Update existing user profile
        await supabaseService.updateUserProfile(editingId, {
          name: formData.name,
          email: formData.email,
          roles: formData.roles
        });
        
        // Find the user to get their auth ID
        const user = users.find(u => u.id === editingId);
        userId = user.user_id || user.id;
      } else {
        // Create new user
        if (!formData.password || formData.password.length < 6) {
          alert('Password must be at least 6 characters long');
          return;
        }

        const result = await supabaseService.signUp(formData.email, formData.password, {
          name: formData.name
        });

        if (result.user) {
          userId = result.user.id;
          // Create user profile
          await supabaseService.createUserProfile(result.user.id, {
            name: formData.name,
            email: formData.email,
            roles: formData.roles
          });
        }
      }

      // Update user permissions
      if (userId) {
        await supabaseService.updateUserPermissions(userId, {
          companies: formData.companyAccess,
          assets: formData.assetPermissions
        });
      }

      await fetchData();
      resetForm();
      alert(editingId ? 'User updated successfully!' : 'User created successfully!');
    } catch (error) {
      console.error('Error saving user:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (user) => {
    setEditingId(user.id);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      roles: user.roles || ['Viewer'],
      companyAccess: user.companyAccess || [],
      assetPermissions: user.assetPermissions || {}
    });
    setShowForm(true);
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await supabaseService.deleteUser(userId);
        await fetchData();
        alert('User deleted successfully!');
      } catch (error) {
        console.error('Error deleting user:', error);
        alert(`Error deleting user: ${error.message}`);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      roles: ['Viewer'],
      companyAccess: [],
      assetPermissions: {}
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleRoleChange = (role, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        roles: [...prev.roles, role]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        roles: prev.roles.filter(r => r !== role)
      }));
    }
  };

  const handleCompanyAccessChange = (companyId, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        companyAccess: [...prev.companyAccess, companyId]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        companyAccess: prev.companyAccess.filter(id => id !== companyId),
        // Remove all asset permissions for this company
        assetPermissions: Object.fromEntries(
          Object.entries(prev.assetPermissions).filter(([assetId]) => {
            // Check if this asset belongs to the removed company
            const company = companies.find(c => c.id === companyId);
            const companyAssets = allAssets[companyId] || [];
            return !companyAssets.some(asset => asset.id === assetId);
          })
        )
      }));
    }
  };

  const handleAssetPermissionChange = (assetId, checked) => {
    setFormData(prev => ({
      ...prev,
      assetPermissions: {
        ...prev.assetPermissions,
        [assetId]: checked
      }
    }));
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Admin': return 'bg-red-100 text-red-800';
      case 'Executive': return 'bg-purple-100 text-purple-800';
      case 'Manager': return 'bg-blue-100 text-blue-800';
      case 'Viewer': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : 'Unknown Company';
  };

  const getUserAccessSummary = (user) => {
    const accessibleCompanies = user.companyAccess || [];
    const accessibleAssets = Object.keys(user.assetPermissions || {}).filter(
      assetId => user.assetPermissions[assetId]
    );
    
    return {
      companies: accessibleCompanies.length,
      assets: accessibleAssets.length
    };
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
              <SafeIcon icon={FiUsers} className="h-8 w-8 text-primary-600" />
              <span>User Management</span>
            </h1>
            <p className="mt-2 text-gray-600">
              Manage user roles, permissions, company access, and asset-level controls.
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <SafeIcon icon={FiRefreshCw} className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="h-4 w-4" />
              <span>Add User</span>
            </button>
          </div>
        </div>
      </motion.div>

      {/* Form Modal */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingId ? 'Edit User' : 'Add New User'}
              </h3>
              <button
                onClick={resetForm}
                disabled={submitting}
                className="text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={FiX} className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter email address"
                    disabled={editingId}
                  />
                </div>
              </div>

              {!editingId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password *
                  </label>
                  <input
                    type="password"
                    required={!editingId}
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter password (min. 6 characters)"
                    minLength={6}
                  />
                </div>
              )}

              {/* Roles */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Roles *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {availableRoles.map((role) => (
                    <label key={role} className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={formData.roles.includes(role)}
                        onChange={(e) => handleRoleChange(role, e.target.checked)}
                        className="text-primary-600 focus:ring-primary-500 rounded"
                      />
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(role)}`}>
                        <SafeIcon icon={FiShield} className="h-3 w-3 mr-1" />
                        {role}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Company Access */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Company Access
                </label>
                <div className="border border-gray-200 rounded-lg p-4 max-h-60 overflow-y-auto">
                  {companies.map((company) => (
                    <div key={company.id} className="mb-4 last:mb-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <input
                          type="checkbox"
                          checked={formData.companyAccess.includes(company.id)}
                          onChange={(e) => handleCompanyAccessChange(company.id, e.target.checked)}
                          className="text-primary-600 focus:ring-primary-500 rounded"
                        />
                        <div className="flex items-center space-x-2">
                          <SafeIcon icon={FiBuilding} className="h-4 w-4 text-gray-500" />
                          <span className="font-medium text-gray-900">{company.name}</span>
                        </div>
                      </div>

                      {/* Asset-level permissions for this company */}
                      {formData.companyAccess.includes(company.id) && allAssets[company.id] && (
                        <div className="ml-6 pl-4 border-l border-gray-200">
                          <div className="text-sm text-gray-600 mb-2">Asset Permissions:</div>
                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {allAssets[company.id].map((asset) => (
                              <label key={asset.id} className="flex items-center space-x-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={formData.assetPermissions[asset.id] || false}
                                  onChange={(e) => handleAssetPermissionChange(asset.id, e.target.checked)}
                                  className="text-primary-600 focus:ring-primary-500 rounded"
                                />
                                <SafeIcon icon={FiBarChart3} className="h-3 w-3 text-gray-400" />
                                <span className="text-gray-700 truncate">{asset.title}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex space-x-3 pt-4 border-t">
                <button
                  type="submit"
                  disabled={submitting || formData.roles.length === 0}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiSave} className="h-4 w-4" />
                      <span>{editingId ? 'Update' : 'Create'}</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}

      {/* Users Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Roles
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Access Summary
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4">
                      <div className="animate-pulse flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                        <div>
                          <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-48"></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="animate-pulse flex space-x-2">
                        <div className="h-6 bg-gray-200 rounded w-16"></div>
                        <div className="h-6 bg-gray-200 rounded w-20"></div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="animate-pulse h-6 bg-gray-200 rounded w-24"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="animate-pulse h-6 bg-gray-200 rounded w-16"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="animate-pulse flex justify-end space-x-2">
                        <div className="w-8 h-8 bg-gray-200 rounded"></div>
                        <div className="w-8 h-8 bg-gray-200 rounded"></div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    No users found. Add your first user to get started.
                  </td>
                </tr>
              ) : (
                users.map((user) => {
                  const accessSummary = getUserAccessSummary(user);
                  const isExpanded = expandedUser === user.id;

                  return (
                    <React.Fragment key={user.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                              <SafeIcon icon={FiUsers} className="h-5 w-5 text-primary-600" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {user.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {(user.roles || ['Viewer']).map((role) => (
                              <span
                                key={role}
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(role)}`}
                              >
                                <SafeIcon icon={FiShield} className="h-3 w-3 mr-1" />
                                {role}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            <div className="flex items-center space-x-4">
                              <span className="flex items-center space-x-1">
                                <SafeIcon icon={FiBuilding} className="h-4 w-4 text-gray-400" />
                                <span>{accessSummary.companies} companies</span>
                              </span>
                              <span className="flex items-center space-x-1">
                                <SafeIcon icon={FiBarChart3} className="h-4 w-4 text-gray-400" />
                                <span>{accessSummary.assets} assets</span>
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor('active')}`}>
                            active
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => setExpandedUser(isExpanded ? null : user.id)}
                              className="text-gray-600 hover:text-gray-700 p-1"
                              title="View Permissions"
                            >
                              <SafeIcon icon={isExpanded ? FiChevronUp : FiChevronDown} className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleEdit(user)}
                              className="text-primary-600 hover:text-primary-700 p-1"
                              title="Edit User"
                            >
                              <SafeIcon icon={FiEdit} className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(user.id)}
                              className="text-red-600 hover:text-red-700 p-1"
                              title="Delete User"
                            >
                              <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded permissions view */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={5} className="px-6 py-4 bg-gray-50">
                            <div className="space-y-4">
                              <h4 className="font-medium text-gray-900">Detailed Permissions</h4>
                              
                              {user.companyAccess && user.companyAccess.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {user.companyAccess.map((companyId) => (
                                    <div key={companyId} className="bg-white rounded-lg border border-gray-200 p-4">
                                      <div className="flex items-center space-x-2 mb-3">
                                        <SafeIcon icon={FiBuilding} className="h-4 w-4 text-primary-600" />
                                        <span className="font-medium text-gray-900">
                                          {getCompanyName(companyId)}
                                        </span>
                                      </div>
                                      
                                      {allAssets[companyId] && (
                                        <div>
                                          <div className="text-sm text-gray-600 mb-2">Accessible Assets:</div>
                                          <div className="space-y-1 max-h-32 overflow-y-auto">
                                            {allAssets[companyId].map((asset) => (
                                              <div
                                                key={asset.id}
                                                className={`flex items-center space-x-2 text-sm p-1 rounded ${
                                                  user.assetPermissions && user.assetPermissions[asset.id]
                                                    ? 'bg-green-50 text-green-700'
                                                    : 'text-gray-400'
                                                }`}
                                              >
                                                <SafeIcon icon={FiBarChart3} className="h-3 w-3" />
                                                <span className="truncate">{asset.title}</span>
                                                {user.assetPermissions && user.assetPermissions[asset.id] && (
                                                  <span className="text-green-600">✓</span>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500 italic">
                                  No company access assigned
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Role-Based Access Control Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6"
      >
        <h3 className="text-lg font-medium text-blue-900 mb-4">Permission System</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-blue-800 mb-2">Role-Based Access</h4>
            <div className="space-y-2">
              {[
                { role: 'Admin', desc: 'Full system access, user management, and configuration' },
                { role: 'Executive', desc: 'Access to all companies and dashboards' },
                { role: 'Manager', desc: 'Access to assigned companies and their assets' },
                { role: 'Viewer', desc: 'Read-only access to assigned company assets' }
              ].map(({ role, desc }) => (
                <div key={role} className="flex items-start space-x-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(role)}`}>
                    <SafeIcon icon={FiShield} className="h-3 w-3 mr-1" />
                    {role}
                  </span>
                  <span className="text-sm text-gray-600">{desc}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-blue-800 mb-2">Granular Permissions</h4>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiBuilding} className="h-4 w-4 text-blue-600" />
                <span>Company-level access control</span>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiBarChart3} className="h-4 w-4 text-blue-600" />
                <span>Asset-level permissions within companies</span>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiShield} className="h-4 w-4 text-blue-600" />
                <span>Role-based overrides for special access</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminUsers;